export const lessonsData = [
  {
    id: 1,
    title: 'Multiplication Concepts',
    vocabulary: [
      { term: 'Multiplication', definition: 'Repeated addition of the same number' },
      { term: 'Factor', definition: 'A number being multiplied' },
      { term: 'Product', definition: 'The answer to a multiplication problem' },
      { term: 'Array', definition: 'Objects arranged in equal rows and columns' },
      { term: 'Skip Counting', definition: 'Counting by a number other than 1' },
      { term: 'Equal Groups', definition: 'Groups that have the same number of objects' }
    ],
    conceptExplanation: `Multiplication is a faster way to add the same number over and over again. Instead of writing 3 + 3 + 3 + 3, we can write 4 × 3, which means "4 groups of 3."

There are several ways to think about multiplication:

**Equal Groups:** If you have 5 bags with 4 apples in each bag, you have 5 × 4 = 20 apples total.

**Arrays:** Objects arranged in rows and columns. If you have 3 rows with 6 dots in each row, that's 3 × 6 = 18 dots.

**Skip Counting:** Counting by the same number repeatedly. To find 7 × 3, you can skip count by 3s seven times: 3, 6, 9, 12, 15, 18, 21.

**The Multiplication Table:** Learning multiplication facts from 0 × 0 up to 10 × 10 helps you solve problems quickly. The order doesn't matter: 4 × 5 = 5 × 4 (this is called the Commutative Property).`,
    examples: [
      {
        problem: 'Example 1: Draw an array and find the product of 4 × 6',
        steps: [
          'Step 1: Draw 4 rows',
          'Step 2: Put 6 dots in each row',
          'Step 3: Count all the dots: 6 + 6 + 6 + 6 = 24',
          'Step 4: Write the multiplication sentence: 4 × 6 = 24'
        ]
      },
      {
        problem: 'Example 2: Use skip counting to find 8 × 3',
        steps: [
          'Step 1: We need to skip count by 3s eight times',
          'Step 2: Count: 3, 6, 9, 12, 15, 18, 21, 24',
          'Step 3: The eighth number is 24',
          'Step 4: So 8 × 3 = 24'
        ]
      },
      {
        problem: 'Example 3: There are 7 boxes. Each box has 5 crayons. How many crayons are there in total?',
        steps: [
          'Step 1: Identify the equal groups: 7 boxes with 5 crayons each',
          'Step 2: Write the multiplication: 7 × 5',
          'Step 3: Use skip counting or the multiplication table: 7 × 5 = 35',
          'Step 4: Answer: There are 35 crayons in total'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is 6 × 7?',
        options: ['36', '42', '48', '54'],
        correctAnswer: 1,
        explanation: '6 × 7 = 42. You can skip count by 7s six times: 7, 14, 21, 28, 35, 42.'
      },
      {
        question: 'A garden has 9 rows of flowers with 4 flowers in each row. How many flowers are there?',
        options: ['13', '32', '36', '45'],
        correctAnswer: 2,
        explanation: '9 rows × 4 flowers = 36 flowers total.'
      },
      {
        question: 'Which multiplication sentence matches this array: ● ● ● ● ● (3 rows of 5 dots)?',
        options: ['5 × 3 = 15', '3 + 5 = 8', '5 + 3 = 8', '3 × 3 = 9'],
        correctAnswer: 0,
        explanation: '3 rows with 5 dots in each row means 3 × 5 = 15 (or 5 × 3 = 15).'
      },
      {
        question: 'What is 0 × 8?',
        options: ['8', '0', '1', '80'],
        correctAnswer: 1,
        explanation: 'Any number multiplied by 0 equals 0. Zero groups of 8 means you have nothing.'
      }
    ],
    teacherTip: 'Practice your multiplication facts every day! Start with the easier ones (×1, ×2, ×5, ×10) and work your way up. Use flashcards, songs, or games to make it fun. Once you know your facts by heart, math becomes so much easier!'
  },
  {
    id: 2,
    title: 'Division Concepts',
    vocabulary: [
      { term: 'Division', definition: 'Splitting a number into equal groups' },
      { term: 'Dividend', definition: 'The number being divided' },
      { term: 'Divisor', definition: 'The number you are dividing by' },
      { term: 'Quotient', definition: 'The answer to a division problem' },
      { term: 'Remainder', definition: 'The amount left over after dividing' },
      { term: 'Fact Family', definition: 'Related multiplication and division facts' }
    ],
    conceptExplanation: `Division is the opposite of multiplication. It means splitting a number into equal groups or finding how many groups you can make.

There are two ways to think about division:

**Sharing Equally:** You have 12 cookies and want to share them equally among 3 friends. How many cookies does each friend get? 12 ÷ 3 = 4 cookies each.

**Repeated Subtraction:** You have 15 apples and put 5 apples in each basket. How many baskets do you need? Keep subtracting 5 until you reach 0: 15 - 5 = 10, 10 - 5 = 5, 5 - 5 = 0. You subtracted 3 times, so 15 ÷ 5 = 3 baskets.

**Division and Multiplication are Related:** If you know that 4 × 6 = 24, then you also know that 24 ÷ 6 = 4 and 24 ÷ 4 = 6. These are called fact families.

**Division Rules:** Any number divided by 1 equals itself (8 ÷ 1 = 8). Any number divided by itself equals 1 (7 ÷ 7 = 1). You cannot divide by 0.`,
    examples: [
      {
        problem: 'Example 1: Solve 20 ÷ 4 using equal groups',
        steps: [
          'Step 1: We need to divide 20 into groups of 4',
          'Step 2: Make groups: 4, 8, 12, 16, 20',
          'Step 3: Count how many groups: 5 groups',
          'Step 4: So 20 ÷ 4 = 5'
        ]
      },
      {
        problem: 'Example 2: Use a fact family to solve 35 ÷ 7',
        steps: [
          'Step 1: Think: What number times 7 equals 35?',
          'Step 2: Recall the multiplication fact: 5 × 7 = 35',
          'Step 3: Use the related division fact: 35 ÷ 7 = 5',
          'Step 4: Answer: 35 ÷ 7 = 5'
        ]
      },
      {
        problem: 'Example 3: There are 18 students. The teacher wants to make teams of 6. How many teams can be made?',
        steps: [
          'Step 1: Identify the division: 18 students ÷ 6 students per team',
          'Step 2: Think: How many 6s are in 18?',
          'Step 3: Use multiplication: 3 × 6 = 18, so 18 ÷ 6 = 3',
          'Step 4: Answer: The teacher can make 3 teams'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is 42 ÷ 6?',
        options: ['6', '7', '8', '9'],
        correctAnswer: 1,
        explanation: '42 ÷ 6 = 7 because 6 × 7 = 42.'
      },
      {
        question: 'A baker has 24 cupcakes and puts 4 cupcakes in each box. How many boxes does he need?',
        options: ['4', '5', '6', '8'],
        correctAnswer: 2,
        explanation: '24 ÷ 4 = 6 boxes. You can check: 6 × 4 = 24.'
      },
      {
        question: 'What is 45 ÷ 5?',
        options: ['7', '8', '9', '10'],
        correctAnswer: 2,
        explanation: '45 ÷ 5 = 9 because 5 × 9 = 45.'
      },
      {
        question: 'Which division sentence is in the same fact family as 8 × 3 = 24?',
        options: ['24 ÷ 4 = 6', '24 ÷ 8 = 3', '24 ÷ 2 = 12', '24 ÷ 6 = 4'],
        correctAnswer: 1,
        explanation: 'The fact family for 8 × 3 = 24 includes 24 ÷ 8 = 3 and 24 ÷ 3 = 8.'
      }
    ],
    teacherTip: 'Division becomes easy when you know your multiplication facts! If you are stuck on a division problem, ask yourself: "What times this number gives me that number?" Drawing pictures or using objects can also help you visualize the problem.'
  },
  {
    id: 3,
    title: 'Fractions',
    vocabulary: [
      { term: 'Fraction', definition: 'A number that represents part of a whole' },
      { term: 'Numerator', definition: 'The top number; shows how many parts you have' },
      { term: 'Denominator', definition: 'The bottom number; shows how many equal parts in the whole' },
      { term: 'Unit Fraction', definition: 'A fraction with 1 as the numerator (like 1/2, 1/3, 1/4)' },
      { term: 'Equivalent Fractions', definition: 'Different fractions that represent the same amount' },
      { term: 'Number Line', definition: 'A line showing numbers in order, including fractions' }
    ],
    conceptExplanation: `A fraction represents part of a whole. The fraction 3/4 means you have 3 parts out of 4 equal parts.

**Understanding Fractions:**
- The denominator (bottom number) tells you how many equal parts the whole is divided into
- The numerator (top number) tells you how many of those parts you have
- Example: In 2/5, the whole is divided into 5 equal parts, and you have 2 of them

**Unit Fractions:** These are fractions with 1 in the numerator, like 1/2, 1/3, 1/4, 1/8. They represent one part of the whole.

**Fractions on a Number Line:** You can show fractions on a number line between 0 and 1. Divide the space into equal parts based on the denominator, then count the parts.

**Equivalent Fractions:** Different fractions can represent the same amount. For example, 1/2 = 2/4 = 3/6. If you cut a pizza into 2 pieces and take 1, that's the same as cutting it into 4 pieces and taking 2.

**Comparing Fractions:** When fractions have the same denominator, compare the numerators. 3/8 is greater than 2/8 because 3 > 2.`,
    examples: [
      {
        problem: 'Example 1: Show 3/4 on a number line',
        steps: [
          'Step 1: Draw a number line from 0 to 1',
          'Step 2: Divide the space into 4 equal parts (because the denominator is 4)',
          'Step 3: Mark each part: 0, 1/4, 2/4, 3/4, 4/4 (which equals 1)',
          'Step 4: Circle 3/4 on the number line'
        ]
      },
      {
        problem: 'Example 2: Find an equivalent fraction for 1/2',
        steps: [
          'Step 1: Draw a rectangle and divide it in half. Shade 1/2',
          'Step 2: Now divide each half in half again (4 parts total)',
          'Step 3: Count the shaded parts: 2 out of 4 are shaded',
          'Step 4: So 1/2 = 2/4 (equivalent fractions)'
        ]
      },
      {
        problem: 'Example 3: Compare 5/8 and 3/8. Which is greater?',
        steps: [
          'Step 1: Notice both fractions have the same denominator (8)',
          'Step 2: When denominators are the same, compare numerators',
          'Step 3: Compare: 5 > 3',
          'Step 4: Therefore, 5/8 > 3/8'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What fraction of this shape is shaded? (Imagine a circle divided into 6 equal parts with 2 parts shaded)',
        options: ['1/6', '2/6', '2/3', '4/6'],
        correctAnswer: 1,
        explanation: '2 parts out of 6 equal parts are shaded, so the fraction is 2/6.'
      },
      {
        question: 'Which fraction is equivalent to 1/3?',
        options: ['2/6', '1/6', '3/6', '2/3'],
        correctAnswer: 0,
        explanation: '1/3 = 2/6. If you multiply both the numerator and denominator by 2, you get 2/6.'
      },
      {
        question: 'Which fraction is greater: 4/5 or 2/5?',
        options: ['4/5', '2/5', 'They are equal', 'Cannot tell'],
        correctAnswer: 0,
        explanation: 'Since both have the same denominator (5), compare numerators: 4 > 2, so 4/5 > 2/5.'
      },
      {
        question: 'On a number line from 0 to 1 divided into eighths, where would 5/8 be located?',
        options: ['Between 0 and 1/4', 'Between 1/4 and 1/2', 'Between 1/2 and 3/4', 'Between 3/4 and 1'],
        correctAnswer: 2,
        explanation: '5/8 is more than 1/2 (which is 4/8) but less than 3/4 (which is 6/8), so it falls between 1/2 and 3/4.'
      }
    ],
    teacherTip: 'Use real objects to understand fractions! Cut sandwiches, pizzas, or paper into equal parts. The more you see and touch fractions in real life, the easier they become. Remember: the bigger the denominator, the smaller each piece is!'
  },
  {
    id: 4,
    title: 'Multiplication & Division Fluency',
    vocabulary: [
      { term: 'Commutative Property', definition: 'You can multiply numbers in any order: 3 × 5 = 5 × 3' },
      { term: 'Associative Property', definition: 'You can group numbers differently: (2 × 3) × 4 = 2 × (3 × 4)' },
      { term: 'Distributive Property', definition: 'Breaking apart numbers to multiply: 7 × 6 = (7 × 5) + (7 × 1)' },
      { term: 'Unknown Factor', definition: 'The missing number in a multiplication equation' },
      { term: 'Two-Step Problem', definition: 'A word problem that requires two operations to solve' },
      { term: 'Inverse Operations', definition: 'Operations that undo each other (multiplication and division)' }
    ],
    conceptExplanation: `Becoming fluent in multiplication and division means solving problems quickly and accurately using different strategies.

**Properties of Multiplication:**

**Commutative Property:** The order doesn't matter. 4 × 7 = 7 × 4 = 28. This means you only need to learn half the multiplication table!

**Associative Property:** When multiplying three numbers, you can group them any way. (2 × 3) × 4 = 2 × (3 × 4) = 24.

**Distributive Property:** Break apart a hard problem into easier ones. To find 8 × 7, think: 8 × 7 = (8 × 5) + (8 × 2) = 40 + 16 = 56.

**Finding Unknown Factors:** If 6 × ? = 48, ask yourself: "6 times what equals 48?" Use division: 48 ÷ 6 = 8, so the unknown factor is 8.

**Two-Step Word Problems:** Some problems require more than one operation. Read carefully, identify what you need to find first, then solve step by step.`,
    examples: [
      {
        problem: 'Example 1: Use the distributive property to find 6 × 8',
        steps: [
          'Step 1: Break 8 into easier numbers: 8 = 5 + 3',
          'Step 2: Multiply each part: 6 × 5 = 30 and 6 × 3 = 18',
          'Step 3: Add the products: 30 + 18 = 48',
          'Step 4: So 6 × 8 = 48'
        ]
      },
      {
        problem: 'Example 2: Find the unknown factor: 7 × ? = 63',
        steps: [
          'Step 1: This is asking: 7 times what number equals 63?',
          'Step 2: Use division to find the unknown: 63 ÷ 7',
          'Step 3: Calculate: 63 ÷ 7 = 9',
          'Step 4: Check your answer: 7 × 9 = 63 ✓'
        ]
      },
      {
        problem: 'Example 3: Sarah bought 4 packs of markers with 8 markers in each pack. She gave 12 markers to her friend. How many markers does Sarah have left?',
        steps: [
          'Step 1: Find the total markers Sarah bought: 4 × 8 = 32 markers',
          'Step 2: Subtract the markers she gave away: 32 - 12',
          'Step 3: Calculate: 32 - 12 = 20',
          'Step 4: Answer: Sarah has 20 markers left'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is the unknown factor? 9 × ? = 72',
        options: ['6', '7', '8', '9'],
        correctAnswer: 2,
        explanation: '72 ÷ 9 = 8, so the unknown factor is 8. Check: 9 × 8 = 72.'
      },
      {
        question: 'A teacher has 5 boxes of pencils with 12 pencils in each box. She gives 15 pencils to students. How many pencils are left?',
        options: ['45', '50', '60', '75'],
        correctAnswer: 0,
        explanation: 'First multiply: 5 × 12 = 60 pencils. Then subtract: 60 - 15 = 45 pencils left.'
      },
      {
        question: 'Which property is shown? 3 × 8 = 8 × 3',
        options: ['Commutative Property', 'Associative Property', 'Distributive Property', 'Identity Property'],
        correctAnswer: 0,
        explanation: 'The Commutative Property says you can multiply numbers in any order and get the same answer.'
      },
      {
        question: 'Use the distributive property: 7 × 9 = (7 × 5) + (7 × ?)',
        options: ['2', '3', '4', '5'],
        correctAnswer: 2,
        explanation: 'Since 9 = 5 + 4, we have 7 × 9 = (7 × 5) + (7 × 4) = 35 + 28 = 63.'
      }
    ],
    teacherTip: 'Master your multiplication facts through daily practice! Use strategies like skip counting, doubling, or breaking numbers apart. When you see a two-step problem, underline the question and circle the important numbers. Solve one step at a time!'
  },
  {
    id: 5,
    title: 'Perimeter & Area',
    vocabulary: [
      { term: 'Perimeter', definition: 'The distance around the outside of a shape' },
      { term: 'Area', definition: 'The amount of space inside a shape' },
      { term: 'Square Unit', definition: 'A square with sides of 1 unit, used to measure area' },
      { term: 'Length', definition: 'The longer side of a rectangle' },
      { term: 'Width', definition: 'The shorter side of a rectangle' },
      { term: 'Formula', definition: 'A rule written with numbers and symbols' }
    ],
    conceptExplanation: `Perimeter and area help us measure shapes in different ways.

**Perimeter** is the distance around the outside of a shape. To find perimeter, add up all the side lengths. Think of it as walking around the edge of a shape.

**Finding Perimeter:**
- For any shape: Add all the sides
- For a rectangle: P = length + length + width + width, or P = 2 × (length + width)
- For a square: P = 4 × side (because all 4 sides are equal)

**Area** is the amount of space inside a shape. We measure area in square units (like square inches or square centimeters).

**Finding Area:**
- Count the square units inside the shape
- For a rectangle: A = length × width
- For a square: A = side × side

**Real-World Uses:**
- Perimeter: How much fencing for a garden, how much ribbon to go around a picture frame
- Area: How much carpet for a room, how much paint for a wall`,
    examples: [
      {
        problem: 'Example 1: Find the perimeter of a rectangle with length 8 cm and width 5 cm',
        steps: [
          'Step 1: Identify all four sides: 8 cm, 5 cm, 8 cm, 5 cm',
          'Step 2: Add all the sides: 8 + 5 + 8 + 5',
          'Step 3: Calculate: 8 + 5 = 13, then 13 + 8 = 21, then 21 + 5 = 26',
          'Step 4: Answer: The perimeter is 26 cm'
        ]
      },
      {
        problem: 'Example 2: Find the area of a rectangle with length 7 inches and width 4 inches',
        steps: [
          'Step 1: Use the formula: Area = length × width',
          'Step 2: Substitute the numbers: A = 7 × 4',
          'Step 3: Multiply: 7 × 4 = 28',
          'Step 4: Answer: The area is 28 square inches'
        ]
      },
      {
        problem: 'Example 3: A square garden has sides of 6 feet. What is its perimeter and area?',
        steps: [
          'Step 1: Find perimeter: P = 4 × side = 4 × 6 = 24 feet',
          'Step 2: Find area: A = side × side = 6 × 6 = 36 square feet',
          'Step 3: Perimeter = 24 feet (distance around)',
          'Step 4: Area = 36 square feet (space inside)'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'What is the perimeter of a rectangle with length 10 cm and width 3 cm?',
        options: ['13 cm', '26 cm', '30 cm', '33 cm'],
        correctAnswer: 1,
        explanation: 'Perimeter = 10 + 3 + 10 + 3 = 26 cm. Or use the formula: P = 2 × (10 + 3) = 2 × 13 = 26 cm.'
      },
      {
        question: 'What is the area of a rectangle with length 9 inches and width 5 inches?',
        options: ['14 square inches', '28 square inches', '45 square inches', '54 square inches'],
        correctAnswer: 2,
        explanation: 'Area = length × width = 9 × 5 = 45 square inches.'
      },
      {
        question: 'A square has a perimeter of 20 meters. What is the length of one side?',
        options: ['4 meters', '5 meters', '10 meters', '80 meters'],
        correctAnswer: 1,
        explanation: 'For a square, P = 4 × side. So 20 = 4 × side. Divide: 20 ÷ 4 = 5 meters per side.'
      },
      {
        question: 'Which has a greater area: a rectangle 6 × 4 or a rectangle 8 × 3?',
        options: ['6 × 4', '8 × 3', 'They are equal', 'Cannot tell'],
        correctAnswer: 2,
        explanation: '6 × 4 = 24 square units and 8 × 3 = 24 square units. They have equal areas!'
      }
    ],
    teacherTip: 'Remember: Perimeter is the distance AROUND (add the sides), and area is the space INSIDE (multiply length × width). Draw pictures to help visualize the problem. Always include units in your answer: cm, inches, feet, etc.!'
  },
  {
    id: 6,
    title: 'Time, Liquid Volume & Mass',
    vocabulary: [
      { term: 'Elapsed Time', definition: 'The amount of time that has passed' },
      { term: 'A.M.', definition: 'Time from midnight to noon (morning)' },
      { term: 'P.M.', definition: 'Time from noon to midnight (afternoon/evening)' },
      { term: 'Liter (L)', definition: 'A metric unit for measuring liquid volume' },
      { term: 'Milliliter (mL)', definition: 'A smaller metric unit; 1,000 mL = 1 L' },
      { term: 'Gram (g)', definition: 'A metric unit for measuring mass (weight)' },
      { term: 'Kilogram (kg)', definition: 'A larger metric unit; 1 kg = 1,000 g' }
    ],
    conceptExplanation: `In third grade, we learn to measure time, liquid volume, and mass using standard units.

**Time and Elapsed Time:**
- Read analog and digital clocks to the nearest minute
- Elapsed time is how much time has passed from start to end
- To find elapsed time: Count forward from the start time to the end time
- Example: From 2:15 PM to 4:30 PM is 2 hours and 15 minutes

**Liquid Volume (Capacity):**
- Measured in liters (L) and milliliters (mL)
- 1 liter = 1,000 milliliters
- A water bottle might hold 500 mL; a large soda bottle holds 2 L
- To convert: multiply liters by 1,000 to get milliliters

**Mass (Weight):**
- Measured in grams (g) and kilograms (kg)
- 1 kilogram = 1,000 grams
- A paperclip weighs about 1 gram; a textbook weighs about 1 kilogram
- To convert: multiply kilograms by 1,000 to get grams

**Choosing the Right Unit:**
- Use mL for small amounts of liquid, L for large amounts
- Use g for light objects, kg for heavy objects`,
    examples: [
      {
        problem: 'Example 1: A movie starts at 3:45 PM and ends at 5:20 PM. How long is the movie?',
        steps: [
          'Step 1: Count from 3:45 PM to 4:00 PM = 15 minutes',
          'Step 2: Count from 4:00 PM to 5:00 PM = 1 hour',
          'Step 3: Count from 5:00 PM to 5:20 PM = 20 minutes',
          'Step 4: Add: 15 minutes + 1 hour + 20 minutes = 1 hour 35 minutes'
        ]
      },
      {
        problem: 'Example 2: Convert 3 liters to milliliters',
        steps: [
          'Step 1: Remember: 1 liter = 1,000 milliliters',
          'Step 2: Multiply: 3 × 1,000',
          'Step 3: Calculate: 3 × 1,000 = 3,000',
          'Step 4: Answer: 3 liters = 3,000 milliliters'
        ]
      },
      {
        problem: 'Example 3: A bag of apples weighs 2 kilograms. How many grams is that?',
        steps: [
          'Step 1: Remember: 1 kilogram = 1,000 grams',
          'Step 2: Multiply: 2 × 1,000',
          'Step 3: Calculate: 2 × 1,000 = 2,000',
          'Step 4: Answer: 2 kilograms = 2,000 grams'
        ]
      }
    ],
    practiceQuestions: [
      {
        question: 'School starts at 8:30 AM and lunch is at 12:00 PM. How much time passes?',
        options: ['3 hours', '3 hours 30 minutes', '4 hours', '4 hours 30 minutes'],
        correctAnswer: 1,
        explanation: 'From 8:30 AM to 12:00 PM is 3 hours and 30 minutes. Count: 8:30 to 9:00 (30 min), 9:00 to 12:00 (3 hours).'
      },
      {
        question: 'How many milliliters are in 5 liters?',
        options: ['50 mL', '500 mL', '5,000 mL', '50,000 mL'],
        correctAnswer: 2,
        explanation: '1 liter = 1,000 mL, so 5 liters = 5 × 1,000 = 5,000 mL.'
      },
      {
        question: 'A watermelon weighs 4 kilograms. How many grams is that?',
        options: ['40 g', '400 g', '4,000 g', '40,000 g'],
        correctAnswer: 2,
        explanation: '1 kilogram = 1,000 grams, so 4 kg = 4 × 1,000 = 4,000 grams.'
      },
      {
        question: 'Which unit would you use to measure the amount of water in a swimming pool?',
        options: ['Milliliters', 'Liters', 'Grams', 'Kilograms'],
        correctAnswer: 1,
        explanation: 'Liters are used for large amounts of liquid. A swimming pool holds thousands of liters!'
      }
    ],
    teacherTip: 'Practice reading clocks throughout the day! For conversions, remember the pattern: 1,000. There are 1,000 mL in 1 L and 1,000 g in 1 kg. When you multiply by 1,000, just add three zeros to the number!'
  }
];